import _ from 'lodash';
import createCachedSelector from 're-reselect';
import { createSelector } from 'reselect';
import { MediaAsset } from '../../types/media-assets';
import { MaybeUndef, StringMap } from '../../types/system';
import { FilesState } from '../files/files.reducer';
import { getMediaAssetsMap } from '../files/files.selectors';
import { StoreState } from '../reducers/store.types';
import {
  defaultFileManagerState,
  FILE_MANAGER_PAGE_COMPONENT_ID,
  FileManagerComponentState,
  SingleFileManagerComponentState,
} from './file-manager-component.types';

import { CurrentFolderState } from './sub-reducers/current-folder.reducer';
import { FilesPaginationState } from './sub-reducers/pagination.reducer';

export const getFileManagerComponentsState = (
  state: StoreState,
): FileManagerComponentState => state.fileManagerComponent;

export const getComponentId = (
  state: StoreState,
  componentId: string,
): string => componentId;

const getComponentById = createSelector(
  getFileManagerComponentsState,
  getComponentId,
  (
    state: FileManagerComponentState,
    componentId: string,
  ): SingleFileManagerComponentState => {
    const component: MaybeUndef<SingleFileManagerComponentState> =
      state[componentId || FILE_MANAGER_PAGE_COMPONENT_ID];
    return component || defaultFileManagerState;
  },
);

export const getCurrentFolderCashed = createCachedSelector(
  getComponentById,
  (component: SingleFileManagerComponentState) => component.currentFolder,
)(getComponentId);

export const getCurrentFolderStateCached = createCachedSelector<
  StoreState,
  string,
  SingleFileManagerComponentState,
  CurrentFolderState
>(
  getComponentById,
  (component: SingleFileManagerComponentState) => component.currentFolder,
)(getComponentId);

export const getCurrentPathCached = createCachedSelector(
  getCurrentFolderStateCached,
  (folder: CurrentFolderState): string => folder.path,
)(getComponentId);

export const getIsCreatingNewFolderCached = createCachedSelector<
  StoreState,
  string,
  SingleFileManagerComponentState,
  boolean
>(
  getComponentById,
  (component: SingleFileManagerComponentState) =>
    component.uiState.isCreatingNewFolder,
)(getComponentId);

export const getPaginationParamsCached = createCachedSelector<
  StoreState,
  string,
  SingleFileManagerComponentState,
  FilesPaginationState
>(
  getComponentById,
  (component: SingleFileManagerComponentState): FilesPaginationState =>
    component.pagination,
)(getComponentId);

export const getIsLoadingMoreCached = createCachedSelector(
  getPaginationParamsCached,
  (pagination) => Boolean(pagination.isLoading && pagination.nextPageToken),
)(getComponentId);

export const getHasMoreFilesCached = createCachedSelector(
  getPaginationParamsCached,
  (pagination: FilesPaginationState): boolean =>
    Boolean(!pagination.isLoading && pagination.nextPageToken),
)(getComponentId);

export const getFilesInCurrentFolderCashed = createCachedSelector<
  StoreState,
  string,
  FilesState,
  CurrentFolderState,
  MediaAsset[]
>(
  getMediaAssetsMap,
  getCurrentFolderCashed,
  (
    filesMap: StringMap<MediaAsset>,
    currentFolder: CurrentFolderState,
  ): MediaAsset[] => _.map(currentFolder.idsList, (id) => filesMap[id]),
)(getComponentId);

export const getFilesIdsByComponent = (
  component: SingleFileManagerComponentState,
): string[] => component.currentFolder.idsList;
